/**
 * WorkspaceShell — the host of the shared document workspace. Owns the float
 * root (the coordinate system of floating windows), renders the recursive
 * split tree (tab strip + windows area per editor group, draggable dividers),
 * mounts floating windows as DOM overlays, draws the snap-zone previews and
 * keeps snapped windows glued to their zone on resize. Ported from pik's
 * CanvasCore window-management render tree.
 *
 * One shell per document. The app mounts it where its document area lives
 * (between the panel dock strips) and provides `renderSurface` to draw each
 * document's body.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useStore } from 'zustand';
import type {
  DocLayoutNode,
  DocumentWorkspace,
  SurfaceRenderContext,
} from '../types';
import { computeWindowLayout, snapZoneGeometry, tileGridStyle } from '../layout/windowLayout';
import { useWorkspaceInteractions } from './useWorkspaceInteractions';
import { DocumentTabStrip } from './DocumentTabStrip';
import { DocumentWindowFrame } from './DocumentWindowFrame';

export interface WorkspaceShellProps {
  workspace: DocumentWorkspace;
  /** Renders one document's body inside its window frame */
  renderSurface: (ctx: SurfaceRenderContext) => ReactNode;
  /** Shown when no tab is open */
  emptyMessage?: string;
  /** Hint in an empty group's strip */
  noDocumentsLabel?: string;
  /** When provided, every group's tab strip renders into this element (a
   * portal) instead of inside its group pane — for hosts that want the strips
   * in their own chrome (e.g. directly under a suite top bar). The element is
   * outside the shell's DOM, but the strips stay fully wired: activation,
   * dragging, overflow and refs all keep working. */
  stripPortalTarget?: HTMLElement | null;
  /** Activation hook — the app swaps its document-scoped state here. The
      shell always updates the store first; the app's own activation flow
      (e.g. pik's snapshot swap) runs after. */
  onActivate?: (id: string) => void;
  /** Close request — routed to the app (dirty guard + save queue live there) */
  onCloseRequest: (id: string) => void;
  /** App-supplied per-tab context menu (right-click on a tab) */
  onTabContextMenu?: (tabId: string, event: ReactMouseEvent) => void;
  /** When true the shell absolutely covers its parent box (pik's canvas
   * overlay mount). Default: fill the parent as a flow child. */
  overlay?: boolean;
}

export const WorkspaceShell = ({
  workspace,
  renderSurface,
  emptyMessage = 'No documents open.',
  noDocumentsLabel = 'No documents',
  stripPortalTarget,
  onActivate,
  onCloseRequest,
  onTabContextMenu,
  overlay = false,
}: WorkspaceShellProps) => {
  const store = workspace.store;
  const { classNames: cn, features } = workspace;
  const {
    tabs,
    tabIds,
    groups,
    layout,
    windowStates,
    arrangement,
    activeTabId,
  } = useStore(store);
  const onTabContextMenuRef = useRef(onTabContextMenu);
  onTabContextMenuRef.current = onTabContextMenu;
  const handleTabContextMenu = useCallback(
    (tabId: string, event: ReactMouseEvent) => {
      onTabContextMenuRef.current?.(tabId, event);
    },
    [],
  );

  const floatRootRef = useRef<HTMLDivElement | null>(null);
  const groupTabRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const registerStripRef = useCallback((groupId: string, el: HTMLDivElement | null) => {
    if (el) {
      groupTabRefs.current.set(groupId, el);
    } else {
      groupTabRefs.current.delete(groupId);
    }
  }, []);

  const interactions = useWorkspaceInteractions({
    store,
    classNames: cn,
    floatRootRef,
    groupTabRefs,
  });

  // Stable activation: chrome state first, then the app's activation flow
  const activateRef = useRef(onActivate);
  activateRef.current = onActivate;
  const closeRequestRef = useRef(onCloseRequest);
  closeRequestRef.current = onCloseRequest;

  const activate = useCallback(
    (id: string) => {
      store.getState().activateTab(id);
      activateRef.current?.(id);
    },
    [store],
  );

  // Stable per-window layout objects: memoized windows only re-render when
  // their own geometry actually changes
  const indexById = useMemo(() => {
    const map = new Map<string, number>();
    tabIds.forEach((id, index) => map.set(id, index));
    return map;
  }, [tabIds]);

  const windowLayouts = useMemo(() => {
    const map: Record<string, ReturnType<typeof computeWindowLayout>> = {};
    tabIds.forEach((id, index) => {
      map[id] = computeWindowLayout(
        arrangement,
        index,
        windowStates[id],
        id === activeTabId,
      );
    });
    return map;
  }, [tabIds, arrangement, windowStates, activeTabId]);

  // Freshly floated tabs (e.g. Window > New Window for Document) get a
  // centered geometry instead of falling back to the cascade offset
  useEffect(() => {
    const areaRect = floatRootRef.current?.getBoundingClientRect();
    if (!areaRect || areaRect.width === 0) return;
    const state = store.getState();
    const missing = tabIds.filter((id) => {
      const ws = state.windowStates[id];
      return ws?.mode === 'float' && !ws.position;
    });
    if (missing.length === 0) return;
    missing.forEach((id, i) => {
      const width = Math.round(areaRect.width * 0.6);
      const height = Math.round(areaRect.height * 0.6);
      store.getState().setWindowState(id, {
        position: {
          x:
            Math.max(0, Math.round((areaRect.width - width) / 2)) +
            i * 24,
          y:
            Math.max(
              0,
              Math.round((areaRect.height - height) / 2),
            ) +
            i * 24,
        },
        size: { width, height },
      });
    });
  }, [tabIds, windowStates, store]);

  // Snapped (docked) windows keep their zone: re-derive the half/quarter
  // geometry whenever the float root itself is resized, so a snapped window
  // stays glued to its edge instead of holding a stale pixel rect
  useEffect(() => {
    const areaEl = floatRootRef.current;
    if (!areaEl || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      // Skip during side-panel or floating-panel resize to avoid unnecessary
      // geometry recalculations that would compete with the drag RAF loop
      if (
        document.body.classList.contains('panel-resizing') ||
        document.body.classList.contains('floating-panel-resizing')
      )
        return;
      const state = store.getState();
      const rect = areaEl.getBoundingClientRect();
      Object.entries(state.windowStates).forEach(([id, ws]) => {
        if (ws.mode !== 'float' || !ws.dockZone) return;
        const geometry = snapZoneGeometry(ws.dockZone, rect.width, rect.height);
        if (
          ws.position?.x !== geometry.position.x ||
          ws.position?.y !== geometry.position.y
        ) {
          state.setWindowState(id, {
            position: geometry.position,
            size: geometry.size,
          });
        }
      });
    });
    observer.observe(areaEl);
    return () => observer.disconnect();
  }, [tabIds.length, arrangement, layout, store]);

  // Opt-in keyboard shortcuts: Ctrl+Tab cycle, Ctrl+W / Ctrl+F4 close
  useEffect(() => {
    if (!features.shortcuts) return;
    const handler = (event: KeyboardEvent) => {
      const state = store.getState();
      const meta = event.ctrlKey || event.metaKey;
      if (meta && event.key === 'Tab') {
        event.preventDefault();
        const ids = state.tabIds;
        if (ids.length < 2) return;
        const current = state.activeTabId ?? ids[0];
        const idx = ids.indexOf(current);
        const next = event.shiftKey
          ? ids[(idx - 1 + ids.length) % ids.length]
          : ids[(idx + 1) % ids.length];
        store.getState().activateTab(next);
        activateRef.current?.(next);
        return;
      }
      if (meta && (event.key === 'w' || event.key === 'W' || event.key === 'F4')) {
        event.preventDefault();
        if (state.activeTabId) closeRequestRef.current(state.activeTabId);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [features.shortcuts, store]);

  const rootIsLeaf = layout.type === 'leaf';
  const floatingTabIds = tabIds.filter(
    (id) => windowStates[id]?.mode === 'float',
  );

  const snapPreviewClassName = (() => {
    if (!interactions.snapZone) return null;
    const fullscreen = tabIds.length <= 1;
    return cn.snapPreview
      ? cn.snapPreview(interactions.snapZone, fullscreen)
      : null;
  })();

  const renderFloatingWindow = (id: string) => {
    const tab = tabs[id];
    if (!tab) return null;
    const layoutResult = windowLayouts[id];
    return (
      <DocumentWindowFrame
        key={id}
        tab={tab}
        index={indexById.get(id) ?? 0}
        isActive={id === activeTabId}
        interactive={layoutResult?.interactive ?? true}
        floating={true}
        style={layoutResult?.style}
        workspace={workspace}
        renderSurface={renderSurface}
        onActivate={activate}
        onCloseRequest={onCloseRequest}
        interactions={interactions}
      />
    );
  };

  const renderNode = (
    node: DocLayoutNode,
    sizePercent?: number,
  ): ReactNode => {
    if (node.type === 'leaf') {
      const group = groups[node.groupId];
      const groupTabIds = group?.tabIds ?? [];
      const groupTabs = groupTabIds
        .map((id) => tabs[id])
        .filter(Boolean);
      const isDockTarget = interactions.dockPreviewGroupId === node.groupId;
      const showTabBar = groupTabs.length > 0 || isDockTarget;
      const paneStyle =
        sizePercent !== undefined
          ? {
              flexBasis: `${sizePercent}%`,
              flexGrow: 0,
              flexShrink: 0,
            }
          : {};

      // Docked windows: every tab of the group keeps its window mounted for
      // canvas state (pik behavior); surfaces marked keepMounted: false only
      // mount their group's active window while tabbed
      const dockedWindows = groupTabs
        .filter((tab) => windowStates[tab.id]?.mode !== 'float')
        .filter((tab) => {
          const def = workspace.getSurface(tab.kind);
          if (def?.keepMounted !== false) return true;
          if (arrangement !== 'tabs') return true;
          return tab.id === (group?.activeTabId ?? null);
        })
        .map((tab) => {
          const layoutResult = windowLayouts[tab.id];
          return (
            <DocumentWindowFrame
              key={tab.id}
              tab={tab}
              index={indexById.get(tab.id) ?? 0}
              isActive={tab.id === activeTabId}
              interactive={layoutResult?.interactive ?? true}
              floating={false}
              style={layoutResult?.style}
              workspace={workspace}
              renderSurface={renderSurface}
              onActivate={activate}
              onCloseRequest={onCloseRequest}
              interactions={interactions}
            />
          );
        });

      const stripElement = showTabBar && (
        <DocumentTabStrip
          groupId={node.groupId}
          tabs={groupTabs}
          activeTabId={activeTabId}
          draggingTabId={interactions.draggingTabId}
          tabDropIndex={interactions.tabDropIndex}
          dockPreviewTab={isDockTarget ? interactions.dockPreviewTab : null}
          interactions={interactions}
          workspace={workspace}
          onCloseRequest={onCloseRequest}
          onActivate={activate}
          noDocumentsLabel={noDocumentsLabel}
          registerRef={registerStripRef}
          onTabContextMenu={handleTabContextMenu}
        />
      );

      return (
        <div key={node.groupId} className={cn.groupPane} style={paneStyle}>
          {stripElement &&
            (stripPortalTarget ? createPortal(stripElement, stripPortalTarget) : stripElement)}
          {/* The windows area — in single-leaf mode this is the float root,
              i.e. the coordinate system for all floating-window positions. */}
          <div
            data-doc-windows-area
            data-doc-arrangement={arrangement}
            ref={rootIsLeaf ? floatRootRef : undefined}
            className={cn.windowsArea?.(arrangement) ?? ''}
            style={arrangement === 'tile' ? tileGridStyle(groupTabs.length) : undefined}
          >
            {dockedWindows}
            {rootIsLeaf && floatingTabIds.map(renderFloatingWindow)}
            {rootIsLeaf && snapPreviewClassName && (
              <div className={snapPreviewClassName} />
            )}
          </div>
        </div>
      );
    }

    // Split node — in split mode the split container is the float root
    return (
      <div
        key={node.id}
        data-doc-split-container={node.direction}
        ref={floatRootRef}
        className={cn.splitContainer?.(node.direction) ?? ''}
      >
        {snapPreviewClassName && <div className={snapPreviewClassName} />}
        {renderNode(node.children[0], node.sizes[0])}
        <div
          data-doc-split-divider={node.direction}
          className={`${cn.splitDivider?.(node.direction) ?? ''}${interactions.splitDividerDragging ? ` ${cn.dividerDragging}` : ''}`}
          onPointerDown={interactions.handleSplitDividerPointerDown(node)}
        />
        {renderNode(node.children[1], node.sizes[1])}
        {/* Floating windows overlay — rendered above both panes, positioned
            relative to the split container */}
        {floatingTabIds.map(renderFloatingWindow)}
      </div>
    );
  };

  return (
    <div
      data-doc-shell
      className={overlay ? 'doc-shell doc-shell--overlay' : 'doc-shell'}
    >
      {tabIds.length === 0 ? (
        <div className={cn.emptyState}>{emptyMessage}</div>
      ) : (
        renderNode(layout)
      )}
    </div>
  );
};
